import React, { useState } from 'react';
import { useNotify, usePermissions, useRefresh, useTranslate } from 'react-admin';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import { apiClient, type ApiError } from '../../utils/apiClient';
import { hasPermission } from '../../utils/hasPermission';
import type { KioskAlert } from './types';
import { announceKioskAlertsChanged } from './useKioskAlertSummary';

export const MarkHandledButton = ({ record }: { record: KioskAlert }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  if (record.status !== 'open' || !hasPermission(permissions as string[] | null | undefined, 'kiosk-alerts:write:all')) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/api/backend/kiosk-alerts/${record.id}/handle`, { note: note.trim() });
      notify(translate('resources.kiosk-alerts.handle.done'), { type: 'success' });
      setOpen(false);
      setNote('');
      refresh();
      announceKioskAlertsChanged();
    } catch (error) {
      const notOpen = (error as ApiError).errorCode === 'KIOSK_ALERT_NOT_OPEN';
      notify(translate(notOpen ? 'resources.kiosk-alerts.handle.notOpen' : 'resources.kiosk-alerts.handle.failed'), { type: 'error' });
      if (notOpen) {
        setOpen(false);
        refresh();
        announceKioskAlertsChanged();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        {translate('resources.kiosk-alerts.handle.button')}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{translate('resources.kiosk-alerts.handle.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{translate('resources.kiosk-alerts.handle.body')}</DialogContentText>
          <TextField
            label={translate('resources.kiosk-alerts.handle.note')}
            helperText={translate('resources.kiosk-alerts.handle.noteHelper')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            inputProps={{ maxLength: 500, 'data-testid': 'handle-note' }}
            multiline
            minRows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>{translate('ra.action.cancel')}</Button>
          <Button variant="contained" onClick={submit} disabled={saving || note.trim() === ''}>
            {translate('resources.kiosk-alerts.handle.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
